/**
 * ActionSheet 滚动锁定：兼容旧 WebView（不依赖 overscroll-behavior）。
 * - position:fixed 锁住底层页面
 * - touchmove + preventDefault 拦截非可滚区域，避免弹层整块被拖动 / 穿透
 */

type SavedBodyStyle = {
  position: string;
  top: string;
  left: string;
  right: string;
  width: string;
  overflow: string;
};

let lockCount = 0;
let lockedScrollY = 0;
let savedBody: SavedBodyStyle | null = null;

function lockBodyScroll() {
  lockCount += 1;
  if (lockCount > 1) return;

  const body = document.body;
  lockedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
  savedBody = {
    position: body.style.position,
    top: body.style.top,
    left: body.style.left,
    right: body.style.right,
    width: body.style.width,
    overflow: body.style.overflow,
  };

  body.style.position = 'fixed';
  body.style.top = `-${lockedScrollY}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';
  body.style.overflow = 'hidden';
}

function unlockBodyScroll() {
  if (lockCount === 0) return;
  lockCount -= 1;
  if (lockCount > 0) return;

  const body = document.body;
  if (savedBody) {
    body.style.position = savedBody.position;
    body.style.top = savedBody.top;
    body.style.left = savedBody.left;
    body.style.right = savedBody.right;
    body.style.width = savedBody.width;
    body.style.overflow = savedBody.overflow;
    savedBody = null;
  }
  window.scrollTo(0, lockedScrollY);
}

function isScrollableY(el: HTMLElement) {
  const style = window.getComputedStyle(el);
  const oy = style.overflowY;
  if (oy !== 'auto' && oy !== 'scroll' && oy !== 'overlay') return false;
  return el.scrollHeight > el.clientHeight + 1;
}

function findScrollParent(target: EventTarget | null, root: HTMLElement): HTMLElement | null {
  let el = target instanceof Element ? (target as HTMLElement) : null;
  while (el && el !== root) {
    if (isScrollableY(el)) return el;
    el = el.parentElement;
  }
  return null;
}

/**
 * 在 sheet 根节点上启用滚动锁与触摸穿透拦截；返回清理函数。
 */
export function enableActionSheetScrollLock(sheetEl: HTMLElement): () => void {
  lockBodyScroll();

  let startY = 0;

  const onTouchStart = (e: TouchEvent) => {
    if (e.touches.length !== 1) return;
    startY = e.touches[0].clientY;
  };

  const onTouchMove = (e: TouchEvent) => {
    if (!e.cancelable || e.touches.length !== 1) return;

    const scrollParent = findScrollParent(e.target, sheetEl);
    if (!scrollParent) {
      e.preventDefault();
      return;
    }

    const deltaY = e.touches[0].clientY - startY;
    const { scrollTop, scrollHeight, clientHeight } = scrollParent;
    const maxScroll = scrollHeight - clientHeight;
    // 到顶继续下拉 / 到底继续上拉 → 拦住，避免带动弹层与底层
    if ((scrollTop <= 0 && deltaY > 0) || (scrollTop >= maxScroll - 1 && deltaY < 0)) {
      e.preventDefault();
    }
  };

  const opts: AddEventListenerOptions = { passive: false, capture: true };
  const startOpts: AddEventListenerOptions = { passive: true, capture: true };
  sheetEl.addEventListener('touchstart', onTouchStart, startOpts);
  sheetEl.addEventListener('touchmove', onTouchMove, opts);

  let cleaned = false;
  return () => {
    if (cleaned) return;
    cleaned = true;
    sheetEl.removeEventListener('touchstart', onTouchStart, startOpts);
    sheetEl.removeEventListener('touchmove', onTouchMove, opts);
    unlockBodyScroll();
  };
}
